import { keyBy } from 'lodash-es';
import { inject } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useFormsStore } from '~/forms/store/forms.store';
import { useTasksStore } from '~/tasks/store/tasks.store';
import { TASK_RESPONSE_FIELDS, useTerraStore } from '~/terra/store/terra.store.js';

export function useTasksFormsComposable() {
  const $services = inject('$services');
  const route = useRoute();

  /**
   * Fetches tasks and forms based on boundary or project filters
   * @param {object} options - Options object
   * @param {boolean} options.tasks - Whether to fetch tasks
   * @param {boolean} options.forms - Whether to fetch forms
   */
  async function fetchProjectsTasksAndForms(options) {
    try {
      const auth_store = useAuthStore();
      const tasks_store = useTasksStore('terra_task_store');
      const forms_store = useFormsStore('terra_form_store');
      const terra_store = useTerraStore();

      if (!terra_store.active_projects?.length) {
        terra_store.projects_loaded_on_map = {};
        tasks_store.tasks_map = {};
        forms_store.forms_map = {};
        return;
      }

      const active_project_uids = terra_store.active_projects.map(item => item.uid);
      // Check which projects need to be fetched
      const projects_to_fetch = [];
      active_project_uids.forEach((project_uid) => {
        // Only fetch if not already fetched (check boolean flag)
        if (!terra_store.projects_loaded_on_map[project_uid]) {
          projects_to_fetch.push(project_uid);
        }
      });

      if (auth_store.has_tasks_access && options.tasks) {
        const tasks_filters = {
          stage: 'TERRA',
          organization: auth_store.current_organization?.uid,
          asset_uid: route.params.asset_id,
          pageSize: Number.MAX_SAFE_INTEGER,
          response_fields: TASK_RESPONSE_FIELDS,
          is_template: false,
          archived: false,
          page_number: 1,
        };
        // Fetch tasks based on boundary or projects
        if (terra_store.should_use_boundary) {
          // Use boundary-based fetching - no need to validate projects
          const response = await $services.tasks.post({
            attribute: 'filters',
            body: {
              filters: {
                ...tasks_filters,
                polygon: terra_store.polygon,
              },
            },
          });

          const tasks = response.data.tasks || [];

          // Store tasks in task_store
          const tasks_by_uid = keyBy(tasks, 'uid');
          tasks_store.tasks_map = tasks_by_uid;
        }
        else if (projects_to_fetch.length > 0) {
          // Use project-based fetching
          // Encode project UIDs as base64 filter
          const encoded = btoa(`projectUid=${projects_to_fetch.join(',')}`);

          // Fetch tasks from API
          const response = await $services.tasks.post({
            attribute: 'filters',
            body: {
              filters: {
                ...tasks_filters,
                properties: encoded,
              },
            },
          });

          const tasks = response.data.tasks || [];

          // Store tasks in task_store
          const tasks_by_uid = keyBy(tasks, 'uid');
          tasks_store.tasks_map = { ...tasks_store.tasks_map, ...tasks_by_uid };
        }

        if (!terra_store.should_use_boundary) {
          // Filter tasks from task_store to only include tasks from active projects
          const filtered_tasks = {};
          Object.values(tasks_store.tasks_map).forEach((task) => {
            const task_project_uid = task.properties?.projectUid || task.properties?.project_uid;
            if (task_project_uid && active_project_uids.includes(task_project_uid)) {
              filtered_tasks[task.uid] = task;
            }
          });

          // Update task_store with only tasks from active projects
          tasks_store.tasks_map = filtered_tasks;
        }
      }

      if (auth_store.has_forms_access && options.forms) {
        const forms_filters = {
          stage: 'TERRA',
          organization: auth_store.current_organization?.uid,
          asset_uid: route.params.asset_id,
          status: 'published',
          is_child: true,
          all_access: true,
          pageSize: Number.MAX_SAFE_INTEGER,
        };
        // Fetch forms based on boundary or projects
        if (terra_store.should_use_boundary) {
          // Use boundary-based fetching - no need to validate projects
          const { data } = await $services.forms.post({
            attribute: 'list-view',
            body: {
              filters: {
                ...forms_filters,
                polygon: terra_store.polygon,
              },
            },
          });

          const forms = data.forms || [];

          // Store forms in forms_store
          const forms_by_uid = keyBy(forms, 'uid');
          forms_store.forms_map = forms_by_uid;
        }
        else if (projects_to_fetch.length > 0) {
          const { data } = await $services.forms.post({
            attribute: 'list-view',
            body: {
              filters: {
                ...forms_filters,
                project_uids: projects_to_fetch,
              },
            },
          });

          const forms = data.forms || [];

          // Store forms in forms_store
          const forms_by_uid = keyBy(forms, 'uid');
          forms_store.forms_map = { ...forms_store.forms_map, ...forms_by_uid };
        }

        if (!terra_store.should_use_boundary) {
          // Filter forms from forms_store to only include forms from active projects
          const filtered_forms = {};
          Object.values(forms_store.forms_map).forEach((form) => {
            const form_project_uid = form.properties?.projectUid || form.properties?.project_uid;
            if (form_project_uid && active_project_uids.includes(form_project_uid)) {
              filtered_forms[form.uid] = form;
            }
          });

          // Update forms_store with only forms from active projects
          forms_store.forms_map = filtered_forms;
        }
      }

      // Mark all active projects as loaded since we fetched by boundary
      active_project_uids.forEach((project_uid) => {
        terra_store.projects_loaded_on_map[project_uid] = true;
      });

      // Clean up projects_loaded_on_map for disabled projects
      Object.keys(terra_store.projects_loaded_on_map).forEach((project_uid) => {
        if (!active_project_uids.includes(project_uid)) {
          delete terra_store.projects_loaded_on_map[project_uid];
        }
      });
    }
    catch (err) {
      logger.error('Error in fetchProjectsTasksAndForms:', err);
      throw err;
    }
  }

  return {
    fetchProjectsTasksAndForms,
  };
}
